import React from "react";
import { TreeDepthOption } from "./DiscussionMap";

interface DiscussionMapControlPanelProps {
  treeDepth: TreeDepthOption;
  setTreeDepth: (depth: TreeDepthOption) => void;
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  focusSelectedBranch: boolean;
  setFocusSelectedBranch: (focus: boolean) => void;
  collapseSiblings: boolean;
  setCollapseSiblings: (collapse: boolean) => void;
  showRecentOnly: boolean;
  setShowRecentOnly: (recent: boolean) => void;
}

const DiscussionMapControlPanel: React.FC<DiscussionMapControlPanelProps> = ({
  treeDepth,
  setTreeDepth,
  isOpen,
  setIsOpen,
  focusSelectedBranch,
  setFocusSelectedBranch,
  collapseSiblings,
  setCollapseSiblings,
  showRecentOnly,
  setShowRecentOnly,
}) => {

  React.useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === "Escape" && isOpen) {
        setIsOpen(false);
      }
    };
    window.addEventListener("keydown", handleEsc);
    return () => window.removeEventListener("keydown", handleEsc);
  }, [isOpen, setIsOpen]);

  return (
    <div 
      className={`discussion-map-panel${isOpen ? " is-open" : ""}`}
      role="dialog"
      aria-label="Discussion Controls"
      aria-hidden={!isOpen}
    >
      {/* Expanded body */}
      {isOpen && (
        <div className="discussion-map-panel-body">
          <div className="discussion-map-panel-body-header">
            <span className="discussion-map-panel-body-title">Controls</span>
            <button
              type="button"
              className="discussion-map-panel-close"
              onClick={() => setIsOpen(false)}
              aria-label="Close controls"
            >
              ×
            </button>
          </div>
          {/* Tree depth */}
          <div className="discussion-map-panel-section">
            <div className="discussion-map-panel-section-label">Tree Depth</div>
            <div className="discussion-map-panel-chips">
              {(["1", "2", "3", "all"] as TreeDepthOption[]).map((opt) => (
                <button
                  key={opt}
                  type="button"
                  className={`discussion-map-panel-chip${treeDepth === opt ? " is-active" : ""}`}
                  onClick={() => setTreeDepth(opt)}
                >
                  {opt === "all" ? "All" : opt}
                </button>
              ))}
            </div>
          </div>

          {/* Focus branch (placeholder) */}
          <div className="discussion-map-panel-section">
            <div className="discussion-map-panel-section-label">Branch</div>
            <button
              type="button"
              className={`discussion-map-panel-action${focusSelectedBranch ? " is-active" : ""}`}
              onClick={() => setFocusSelectedBranch(!focusSelectedBranch)}
              title="Focus selected branch"
            >
              Focus selected branch
            </button>
            <button
              type="button"
              className={`discussion-map-panel-action${collapseSiblings ? " is-active" : ""}`}
              onClick={() => setCollapseSiblings(!collapseSiblings)}
              title="Collapse siblings"
            >
              Collapse siblings
            </button>
          </div>

          {/* Activity filter */}
          <div className="discussion-map-panel-section">
            <div className="discussion-map-panel-section-label">Activity Filter</div>
            <button
              type="button"
              className={`discussion-map-panel-action${showRecentOnly ? " is-active" : ""}`}
              onClick={() => setShowRecentOnly(!showRecentOnly)}
              title="Show recent only"
            >
              Show recent only
            </button>
          </div>

          {/* Legend */}
          <div className="discussion-map-panel-section">
            <div className="discussion-map-panel-section-label">Legend</div>
            <div className="discussion-map-panel-legend">
              <span className="discussion-map-panel-legend-item">
                <span className="discussion-map-panel-legend-dot is-active" /> Selected
              </span>
              <span className="discussion-map-panel-legend-item">
                <span className="discussion-map-panel-legend-dot is-reply" /> Reply
              </span>
              <span className="discussion-map-panel-legend-item">
                <span className="discussion-map-panel-legend-badge">v</span> Versions
              </span>
              <span className="discussion-map-panel-legend-item">
                <span className="discussion-map-panel-legend-badge is-comment">💬</span> Comments
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DiscussionMapControlPanel;
